/**
 * TD Header Nav
 *
 * Behaviour for sections/td-header-nav.liquid:
 *   - desktop: one open panel at a time, opened on click or hover, closed on
 *     Escape, outside click, or focus leaving the header
 *   - desktop: an in-panel search field that takes over the panel area
 *   - mobile: a full-screen drawer with a single-level drilldown
 *
 * The active panel's colour is written to the shell as a custom property so the
 * bar and panel share one background, matching the Figma "nav-open" frame.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('td-header-nav')) {
    return;
  }

  var DESKTOP_QUERY = '(min-width: 769px)';
  var HOVER_CLOSE_DELAY = 180;

  function TDHeaderNavFactory() {
    return class TDHeaderNav extends HTMLElement {
      constructor() {
        super();
        this.openPanelId = null;
        this.searchOpen = false;
        this.drawerOpen = false;
        this.hoverTimer = null;

        this.onBarClick = this.onBarClick.bind(this);
        this.onPointerOver = this.onPointerOver.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
      }

      connectedCallback() {
        this.shell = this.querySelector('[data-td-nav-shell]');
        this.triggers = Array.prototype.slice.call(
          this.querySelectorAll('[data-td-nav-trigger]')
        );
        this.panels = Array.prototype.slice.call(
          this.querySelectorAll('[data-td-nav-panel]')
        );
        this.searchEl = this.querySelector('[data-td-nav-search]');
        this.drawer = this.querySelector('[data-td-nav-drawer]');
        this.screens = Array.prototype.slice.call(
          this.querySelectorAll('[data-td-nav-screen]')
        );
        this.desktopMedia = window.matchMedia(DESKTOP_QUERY);

        this.addEventListener('click', this.onBarClick);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('focusout', this.onFocusOut);

        if (this.dataset.openOn === 'hover') {
          this.addEventListener('pointerover', this.onPointerOver);
          this.addEventListener('pointerleave', this.onPointerLeave);
        }

        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        window.clearTimeout(this.hoverTimer);
        this.unlockScroll();
      }

      /* ------------------------------------------------------------------ */
      /* Desktop panels                                                      */
      /* ------------------------------------------------------------------ */

      isDesktop() {
        return this.desktopMedia.matches;
      }

      openPanel(id) {
        if (this.openPanelId === id) {
          return;
        }

        this.closeSearch();
        this.openPanelId = id;

        var activeColor = null;

        this.triggers.forEach(function (trigger) {
          var isActive = trigger.dataset.tdNavTrigger === id;
          trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
          if (isActive && trigger.dataset.tdNavColor) {
            activeColor = trigger.dataset.tdNavColor;
          }
        });

        this.panels.forEach(function (panel) {
          panel.hidden = panel.dataset.tdNavPanel !== id;
        });

        this.setShellColor(activeColor);
        this.classList.add('td-nav--open', 'td-nav--panel-open');
      }

      closePanel() {
        if (this.openPanelId === null) {
          return;
        }
        this.openPanelId = null;

        this.triggers.forEach(function (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        });
        this.panels.forEach(function (panel) {
          panel.hidden = true;
        });

        this.setShellColor(null);
        this.classList.remove('td-nav--panel-open');
        if (!this.searchOpen) {
          this.classList.remove('td-nav--open');
        }
      }

      togglePanel(id) {
        if (this.openPanelId === id) {
          this.closePanel();
        } else {
          this.openPanel(id);
        }
      }

      setShellColor(color) {
        if (!this.shell) {
          return;
        }
        if (color) {
          this.shell.style.setProperty('--td-nav-bg', color);
        } else {
          this.shell.style.removeProperty('--td-nav-bg');
        }
      }

      /* ------------------------------------------------------------------ */
      /* Desktop search                                                      */
      /* ------------------------------------------------------------------ */

      openSearch() {
        if (!this.searchEl || this.searchOpen) {
          return;
        }
        this.closePanel();
        this.searchOpen = true;
        this.searchEl.hidden = false;
        this.classList.add('td-nav--open');

        var toggle = this.querySelector('[data-td-nav-search-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'true');
        }

        var input = this.searchEl.querySelector('.td-nav__search-input');
        if (input) {
          input.focus();
        }
      }

      closeSearch(returnFocus) {
        if (!this.searchEl || !this.searchOpen) {
          return;
        }
        this.searchOpen = false;
        this.searchEl.hidden = true;

        var toggle = this.querySelector('[data-td-nav-search-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
          if (returnFocus) {
            toggle.focus();
          }
        }

        if (this.openPanelId === null) {
          this.classList.remove('td-nav--open');
        }
      }

      /* ------------------------------------------------------------------ */
      /* Mobile drawer                                                       */
      /* ------------------------------------------------------------------ */

      openDrawer() {
        if (!this.drawer || this.drawerOpen) {
          return;
        }
        this.drawerOpen = true;
        this.drawer.hidden = false;
        this.showScreen('root');
        this.lockScroll();

        var opener = this.querySelector('[data-td-nav-drawer-open]');
        if (opener) {
          opener.setAttribute('aria-expanded', 'true');
        }

        var closer = this.drawer.querySelector('[data-td-nav-drawer-close]');
        if (closer) {
          closer.focus();
        }
      }

      closeDrawer(returnFocus) {
        if (!this.drawer || !this.drawerOpen) {
          return;
        }
        this.drawerOpen = false;
        this.drawer.hidden = true;
        this.drawer.style.removeProperty('--td-nav-active-color');
        this.unlockScroll();

        var opener = this.querySelector('[data-td-nav-drawer-open]');
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          if (returnFocus) {
            opener.focus();
          }
        }
      }

      showScreen(id) {
        this.screens.forEach(function (screen) {
          screen.hidden = screen.dataset.tdNavScreen !== id;
        });
      }

      drilldown(id, color) {
        if (!this.drawer) {
          return;
        }
        if (color) {
          this.drawer.style.setProperty('--td-nav-active-color', color);
        } else {
          this.drawer.style.removeProperty('--td-nav-active-color');
        }
        this.showScreen(id);

        var screen = this.drawer.querySelector(
          '[data-td-nav-screen="' + id + '"]'
        );
        var back = screen && screen.querySelector('[data-td-nav-back]');
        if (back) {
          back.focus();
        }
      }

      goBack() {
        this.showScreen('root');
        if (this.drawer) {
          this.drawer.style.removeProperty('--td-nav-active-color');
        }
        var first = this.drawer && this.drawer.querySelector('[data-td-nav-drilldown]');
        if (first) {
          first.focus();
        }
      }

      lockScroll() {
        document.body.classList.add('td-nav-drawer-open');
      }

      unlockScroll() {
        document.body.classList.remove('td-nav-drawer-open');
      }

      /* ------------------------------------------------------------------ */
      /* Events                                                              */
      /* ------------------------------------------------------------------ */

      onBarClick(event) {
        var target = event.target;
        if (!(target instanceof Element)) {
          return;
        }

        var trigger = target.closest('[data-td-nav-trigger]');
        if (trigger && this.contains(trigger)) {
          event.preventDefault();
          this.togglePanel(trigger.dataset.tdNavTrigger);
          return;
        }

        if (target.closest('[data-td-nav-search-toggle]')) {
          event.preventDefault();
          if (this.searchOpen) {
            this.closeSearch(true);
          } else {
            this.openSearch();
          }
          return;
        }

        if (target.closest('[data-td-nav-search-close]')) {
          event.preventDefault();
          this.closeSearch(true);
          return;
        }

        if (target.closest('[data-td-nav-drawer-open]')) {
          event.preventDefault();
          this.openDrawer();
          return;
        }

        if (target.closest('[data-td-nav-drawer-close]')) {
          event.preventDefault();
          this.closeDrawer(true);
          return;
        }

        var drill = target.closest('[data-td-nav-drilldown]');
        if (drill) {
          event.preventDefault();
          this.drilldown(drill.dataset.tdNavDrilldown, drill.dataset.tdNavColor);
          return;
        }

        if (target.closest('[data-td-nav-back]')) {
          event.preventDefault();
          this.goBack();
        }
      }

      onPointerOver(event) {
        if (!this.isDesktop() || event.pointerType === 'touch') {
          return;
        }
        window.clearTimeout(this.hoverTimer);

        var target = event.target;
        if (!(target instanceof Element)) {
          return;
        }
        var trigger = target.closest('[data-td-nav-trigger]');
        if (trigger) {
          this.openPanel(trigger.dataset.tdNavTrigger);
        }
      }

      onPointerLeave(event) {
        if (!this.isDesktop() || event.pointerType === 'touch') {
          return;
        }
        var self = this;
        window.clearTimeout(this.hoverTimer);
        this.hoverTimer = window.setTimeout(function () {
          self.closePanel();
        }, HOVER_CLOSE_DELAY);
      }

      onKeydown(event) {
        if (event.key !== 'Escape') {
          return;
        }
        if (this.drawerOpen) {
          this.closeDrawer(true);
          return;
        }
        if (this.searchOpen) {
          this.closeSearch(true);
          return;
        }
        if (this.openPanelId !== null) {
          var active = this.querySelector(
            '[data-td-nav-trigger="' + this.openPanelId + '"]'
          );
          this.closePanel();
          if (active) {
            active.focus();
          }
        }
      }

      onDocumentClick(event) {
        if (this.openPanelId === null && !this.searchOpen) {
          return;
        }
        if (event.target instanceof Node && this.contains(event.target)) {
          return;
        }
        this.closePanel();
        this.closeSearch();
      }

      onFocusOut(event) {
        if (this.openPanelId === null && !this.searchOpen) {
          return;
        }
        var next = event.relatedTarget;
        if (next && this.contains(next)) {
          return;
        }
        // Focus moved out of the header entirely; collapse the open state.
        if (!this.drawerOpen) {
          this.closePanel();
          this.closeSearch();
        }
      }
    };
  }

  customElements.define('td-header-nav', TDHeaderNavFactory());
})();
